// ─── FieldMappingModal.tsx ───────────────────────────────────────────────────

import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { DB_FIELDS, FieldMapping, StudentData, UDISERow, sel } from './types';

interface FieldMappingModalProps {
  visible: boolean;
  onHide: () => void;
  onBack: () => void;
  udiseRows: UDISERow[];
  udiseHeaders: string[];
  students: StudentData[];
  mappings: FieldMapping[];
  onMappingsChange: (m: FieldMapping[]) => void;
  matchField: string;
  onMatchFieldChange: (f: string) => void;
  udiseMatchField: string;
  onUdiseMatchFieldChange: (f: string) => void;
  onRunComparison: () => void;
}

const FieldMappingModal: React.FC<FieldMappingModalProps> = ({
  visible, onHide, onBack,
  udiseRows, udiseHeaders, students,
  mappings, onMappingsChange,
  matchField, onMatchFieldChange,
  udiseMatchField, onUdiseMatchFieldChange,
  onRunComparison,
}) => {
  const updateMapping = (idx: number, patch: Partial<FieldMapping>) => {
    const next = [...mappings];
    next[idx] = { ...next[idx], ...patch };
    onMappingsChange(next);
  };

  const hasStudents = students.length > 0;
  const enabledCount = mappings.filter(m => m.enabled && m.dbField && m.udiseField).length;

  const header = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '4px 0' }}>
      <div style={{
        width: 48, height: 48, borderRadius: 13,
        background: 'linear-gradient(135deg,#1e3a8a,#3b82f6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, boxShadow: '0 6px 18px rgba(59,130,246,.25)',
        border: '2.5px solid white',
      }}>
        <i className="pi pi-arrows-h" style={{ fontSize: 20, color: 'white' }} />
      </div>
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#1e40af' }}>Map Fields</div>
        <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
          Step 2 of 2 &nbsp;·&nbsp; {udiseRows.length} UDISE rows &nbsp;·&nbsp; {students.length} DB students
        </div>
      </div>
      <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
        <div style={{ width: 28, height: 6, borderRadius: 3, background: '#dbeafe' }} />
        <div style={{ width: 28, height: 6, borderRadius: 3, background: '#3b82f6' }} />
      </div>
    </div>
  );

  return (
    <Dialog
      visible={visible} onHide={onHide} header={header}
      style={{ width: '94vw', maxWidth: 900, borderRadius: 22, overflow: 'hidden' }}
      contentStyle={{ padding: '18px 24px 8px', background: 'linear-gradient(180deg,#f8fafc,#f1f5f9)' }}
      headerStyle={{ background: 'white', borderBottom: '1px solid #e5e7eb', padding: '18px 24px', borderRadius: '22px 22px 0 0' }}
      modal draggable={false} resizable={false} blockScroll dismissableMask
    >
      <style>{`
        .fm-select {
          width: 100%; padding: 8px 10px; border-radius: 10px;
          border: 1.5px solid #dbeafe; background: white;
          font-size: 13px; color: #1e3a8a; outline: none; cursor: pointer;
          transition: border-color .15s;
        }
        .fm-select:focus { border-color: #3b82f6; }
        .fm-field-label {
          font-size: 11px; font-weight: 600; color: #64748b;
          display: block; margin-bottom: 5px;
          text-transform: uppercase; letter-spacing: .4px;
        }
        .fm-row {
          display: grid; grid-template-columns: 22px 1fr 22px 1fr 1fr 22px;
          gap: 8px; align-items: center;
          padding: 10px 13px; border-radius: 12px;
          border: 1.5px solid #e5e7eb; background: white;
          transition: all .15s;
        }
        .fm-row.enabled  { border-color: #dbeafe; }
        .fm-row.disabled { border-color: #f1f5f9; background: #f8fafc; opacity: .55; }
        .fm-checkbox {
          width: 18px; height: 18px; border-radius: 5px;
          border: 1.5px solid #d1d5db; background: white;
          flex-shrink: 0; display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all .12s;
        }
        .fm-checkbox.checked { background: #3b82f6; border-color: #3b82f6; }
        .fm-input {
          width: 100%; padding: 8px 10px; border-radius: 10px;
          border: 1.5px solid #e5e7eb; background: white;
          font-size: 13px; color: #374151; outline: none;
          transition: border-color .15s;
        }
        .fm-input:focus { border-color: #3b82f6; }
        .fm-add-btn {
          width: 100%; padding: 10px; border-radius: 12px;
          border: 1.5px dashed #bfdbfe; background: #f0f9ff; color: #3b82f6;
          font-size: 13px; font-weight: 600; cursor: pointer;
          display: flex; align-items: center; justify-content: center; gap: 6px;
          transition: all .15s;
        }
        .fm-add-btn:hover { border-color: #3b82f6; background: #dbeafe; }
        .fm-del-btn {
          background: none; border: none; cursor: pointer;
          color: #ef4444; padding: 2px; display: flex; align-items: center;
          border-radius: 6px; transition: background .12s;
        }
        .fm-del-btn:hover { background: #fee2e2; }
      `}</style>

      {/* Match-by card */}
      <div style={{
        padding: '14px 16px', borderRadius: 14,
        background: 'white', border: '1.5px solid #e5e7eb',
        boxShadow: '0 1px 4px rgba(0,0,0,.04)', marginBottom: 16,
      }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1e40af', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 7 }}>
          <i className="pi pi-link" style={{ fontSize: 13, color: '#3b82f6' }} />
          Match students by:
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label className="fm-field-label">DB Field</label>
            <select className="fm-select" value={matchField} onChange={e => onMatchFieldChange(e.target.value)}>
              {DB_FIELDS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
          </div>
          <div>
            <label className="fm-field-label">CSV Column</label>
            <select className="fm-select" value={udiseMatchField} onChange={e => onUdiseMatchFieldChange(e.target.value)}>
              {udiseHeaders.map(h => <option key={h} value={h}>{h}</option>)}
            </select>
          </div>
        </div>
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 8 }}>
          <i className="pi pi-info-circle" style={{ marginRight: 4, color: '#3b82f6' }} />
          Use Name or Admission No for best results.
        </div>
      </div>

      {/* Fields to compare heading + count */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1e40af', display: 'flex', alignItems: 'center', gap: 7 }}>
          <i className="pi pi-sliders-h" style={{ fontSize: 13, color: '#3b82f6' }} />
          Fields to Compare
        </div>
        <span style={{
          fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 20,
          background: enabledCount > 0 ? 'rgba(59,130,246,.12)' : '#f3f4f6',
          color: enabledCount > 0 ? '#1e40af' : '#9ca3af',
        }}>
          {enabledCount} active
        </span>
      </div>

      {/* Mapping rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 240, overflowY: 'auto', paddingRight: 2, marginBottom: 10 }}>
        {mappings.map((m, idx) => (
          <div key={idx} className={`fm-row ${m.enabled ? 'enabled' : 'disabled'}`}>
            {/* Checkbox */}
            <div
              className={`fm-checkbox ${m.enabled ? 'checked' : ''}`}
              onClick={() => updateMapping(idx, { enabled: !m.enabled })}
            >
              {m.enabled && <i className="pi pi-check" style={{ fontSize: 9, color: 'white' }} />}
            </div>

            {/* DB field select */}
            <select
              className="fm-select"
              value={m.dbField}
              onChange={e => updateMapping(idx, { dbField: e.target.value as keyof StudentData })}
            >
              <option value="">-- DB Field --</option>
              {DB_FIELDS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>

            {/* Arrow icon */}
            <div style={{ display: 'flex', justifyContent: 'center', color: '#94a3b8' }}>
              <i className="pi pi-arrows-h" style={{ fontSize: 10 }} />
            </div>

            {/* CSV column select */}
            <select
              className="fm-select"
              value={m.udiseField}
              onChange={e => updateMapping(idx, { udiseField: e.target.value })}
            >
              <option value="">-- CSV Column --</option>
              {udiseHeaders.map(h => <option key={h} value={h}>{h}</option>)}
            </select>

            {/* Label input */}
            <input
              type="text" placeholder="Label (optional)"
              value={m.label}
              onChange={e => updateMapping(idx, { label: e.target.value })}
              className="fm-input"
            />

            {/* Delete */}
            <button
              className="fm-del-btn"
              onClick={() => onMappingsChange(mappings.filter((_, i) => i !== idx))}
            >
              <i className="pi pi-times" style={{ fontSize: 11 }} />
            </button>
          </div>
        ))}
      </div>

      {/* Add field button */}
      <button
        className="fm-add-btn"
        style={{ marginBottom: 18 }}
        onClick={() => onMappingsChange([...mappings, { dbField: '', udiseField: udiseHeaders[0] ?? '', label: '', enabled: true }])}
      >
        <i className="pi pi-plus" style={{ fontSize: 11 }} /> Add Comparison Field
      </button>

      {/* Footer */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0 6px', borderTop: '1px solid #f0f0f0' }}>
        <div style={{ fontSize: 13, color: '#64748b' }}>
          {enabledCount === 0
            ? 'No fields selected'
            : <><span style={{ fontWeight: 700, color: '#3b82f6' }}>{enabledCount}</span> field{enabledCount !== 1 ? 's' : ''} will be compared</>
          }
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button label="← Back" icon="pi pi-arrow-left" className="p-button-outlined"
            style={{ borderRadius: 10, fontSize: 13 }} onClick={onBack} />
          <Button label="Cancel" icon="pi pi-times" className="p-button-outlined"
            style={{ borderRadius: 10, fontSize: 13 }} onClick={onHide} />
          <Button
            label="Run Comparison →" icon="pi pi-play" iconPos="right"
            disabled={!hasStudents || enabledCount === 0}
            style={{
              borderRadius: 10, fontSize: 13,
              background: 'linear-gradient(135deg,#1e40af,#3b82f6)', border: 'none',
              opacity: (!hasStudents || enabledCount === 0) ? 0.45 : 1,
              transition: 'opacity .15s',
            }}
            onClick={onRunComparison}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default FieldMappingModal;